import type { JSX } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { Building2, Globe, Home, Inbox, Package, ScrollText, ShieldCheck, Users } from "lucide-react";
import { AppShell, Button, Toaster, type NavGroup } from "@nahal/ui";
import { useSession } from "../lib/session";
import { SignInScreen } from "../features/auth/SignInScreen";
import { HomeScreen } from "../features/home/HomeScreen";
import { OrganizationScreen } from "../features/organization/OrganizationScreen";
import { MembersScreen } from "../features/members/MembersScreen";
import { ProductsScreen } from "../features/products/ProductsScreen";
import { ProductDetailScreen } from "../features/products/ProductDetailScreen";
import { RequestsScreen } from "../features/requests/RequestsScreen";
import { ActivityScreen } from "../features/activity/ActivityScreen";
import { SecurityScreen } from "../features/security/SecurityScreen";
import { DomainsScreen } from "../features/domains/DomainsScreen";

const nav: NavGroup[] = [
  {
    key: "workspace",
    label: "Your account",
    items: [
      { key: "home", label: "Home", href: "/", icon: <Home className="size-5" /> },
      { key: "products", label: "Products", href: "/products", icon: <Package className="size-5" /> },
      { key: "requests", label: "Requests", href: "/requests", icon: <Inbox className="size-5" /> },
      { key: "domains", label: "Domains", href: "/domains", icon: <Globe className="size-5" /> },
    ],
  },
  {
    key: "organization",
    label: "Organisation",
    items: [
      {
        key: "organization",
        label: "Details",
        href: "/organization",
        icon: <Building2 className="size-5" />,
      },
      { key: "members", label: "Members", href: "/members", icon: <Users className="size-5" /> },
      {
        key: "activity",
        label: "Activity",
        href: "/activity",
        icon: <ScrollText className="size-5" />,
      },
      {
        key: "security",
        label: "Security",
        href: "/security",
        icon: <ShieldCheck className="size-5" />,
      },
    ],
  },
];

const activeKeyFor = (pathname: string) =>
  pathname === "/" ? "home" : (pathname.replace(/^\//, "").split("/")[0] ?? "home");

function Shell() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const refreshToken = useSession((state) => state.refreshToken);
  const accessToken = useSession((state) => state.accessToken);
  const clear = useSession((state) => state.clear);

  if (!refreshToken || !accessToken) {
    return (
      <>
        <SignInScreen />
        <Toaster />
      </>
    );
  }

  return (
    <>
      <AppShell
        surface="console"
        productName="Nahal Labs"
        nav={nav}
        activeKey={activeKeyFor(pathname)}
        onNavigate={(href) => navigate({ to: href })}
        primaryNavKeys={["home", "products", "requests", "members"]}
        headerRight={
          <Button size="sm" variant="ghost" onClick={clear}>
            Sign out
          </Button>
        }
      >
        <Outlet />
      </AppShell>
      <Toaster />
    </>
  );
}

const rootRoute = createRootRoute({ component: Shell });

const screen = (path: string, component: () => JSX.Element) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

const routeTree = rootRoute.addChildren([
  screen("/", HomeScreen),
  screen("/products", ProductsScreen),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/products/$productCode",
    component: ProductDetailScreen,
  }),
  screen("/requests", RequestsScreen),
  screen("/organization", OrganizationScreen),
  screen("/members", MembersScreen),
  screen("/activity", ActivityScreen),
  screen("/security", SecurityScreen),
  screen("/domains", DomainsScreen),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
